import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Cross } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { BUSINESS, telHref } from "@/data/site";
import { scrollToId, scrollToTop } from "@/lib/scroll";

const LINKS = [
  { id: "about", label: "About", tid: NAV.linkAbout },
  { id: "categories", label: "Categories", tid: NAV.linkCategories },
  { id: "prescription", label: "Upload Rx", tid: "nav-link-prescription" },
  { id: "faq", label: "FAQ", tid: NAV.linkFaq },
  { id: "contact", label: "Contact", tid: NAV.linkContact },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,padding] duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <button
          data-testid={NAV.logo}
          onClick={scrollToTop}
          className="flex items-center gap-2.5 group"
        >
          <span className="grid place-items-center h-10 w-10 rounded-2xl bg-brand-blue text-white shadow-lg shadow-brand-blue/20 transition-transform group-hover:-rotate-6">
            <Cross className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="text-left leading-none">
            <span className="block font-heading font-bold text-[15px] tracking-tight text-ink">
              PALIWAL
            </span>
            <span className="block font-heading text-[11px] font-medium tracking-[0.28em] text-brand-green">
              MEDICOS
            </span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.tid}
              onClick={() => go(l.id)}
              className="relative px-4 py-2 text-sm font-medium text-slate-600 hover:text-brand-blue transition-colors group"
            >
              {l.label}
              <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 origin-left scale-x-0 bg-brand-green transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            data-testid={NAV.callBtn}
            href={telHref}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-blue/20 transition-all hover:bg-brand-blue-dark hover:shadow-brand-blue/30 active:scale-95"
          >
            <Phone className="h-4 w-4" />
            {BUSINESS.phoneDisplay}
          </a>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-slate-200 bg-white/70 text-ink"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-slate-200/70"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-base font-medium text-slate-700 border-b border-slate-100 last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={telHref}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-5 py-3 text-sm font-semibold text-white"
              >
                <Phone className="h-4 w-4" /> Call {BUSINESS.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
